import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';

import { LanguageFlag } from '@/components/atoms/FlagEmoji/LanguageFlag';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { languageByCountryCode } from '@/shared/languageCodes';
import { AIAgent } from '@/shared/types/brain';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';
import { formatLanguageLabel } from '@/util/languageOptions';

import ActionsCell from './ActionsCell';
import { FavoriteCell } from './FavoriteCell';
import { NameCell } from './NameCell';
import { StartingNodeCell } from './StartingNodeCell';
import { ToolsCell } from './ToolsCell';
import { AIAgentIcon } from '../../icons/AIAgentIcon';

interface ColumnsProps {
  toggleFavorite: (event: React.MouseEvent<HTMLElement>, aiAgentId: string) => void;
  favoriteIds: string[];
}

export const columns = ({ toggleFavorite, favoriteIds }: ColumnsProps): ColumnDef<AIAgent>[] => [
  {
    id: 'favorite',
    enableHiding: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
    cell: ({ row }) => {
      const aiAgent = row.original;
      const isFavorite = favoriteIds.includes(aiAgent.brain_id);

      return (
        <FavoriteCell aiAgent={aiAgent} isFavorite={isFavorite} toggleFavorite={toggleFavorite} />
      );
    },
  },
  {
    id: 'name',
    accessorKey: 'name',
    enableHiding: false,
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX' },
    header: ({ column }) => <SortableHeader column={column}>{t('ai_agents.agent')}</SortableHeader>,
    cell: ({ row }) => {
      const agent = row.original;
      return <NameCell agent={agent} />;
    },
  },
  {
    id: 'brain_type',
    accessorFn: (row) => t(`ai_agents.types.${row.brain_type}.title`),
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'ai_agents.agent_type' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('ai_agents.agent_type')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const brainType = row.original.brain_type;
      return (
        <div className="flex items-center gap-3">
          <AIAgentIcon type={brainType} size={16} />
          <span className="truncate font-medium">{t(`ai_agents.types.${brainType}.title`)}</span>
        </div>
      );
    },
  },
  {
    id: 'language',
    accessorFn: (row) => languageByCountryCode[row.language] ?? row.language,
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'field.language' },
    header: ({ column }) => <SortableHeader column={column}>{t('field.language')}</SortableHeader>,
    cell: ({ row }) => {
      const language = row.original.language;
      const formattedLanguageLabel = formatLanguageLabel(language);
      return (
        <InfoTooltip content={formattedLanguageLabel} side="top">
          <div className="flex items-center gap-2">
            <LanguageFlag code={language} />
            <span className="truncate">{formattedLanguageLabel}</span>
          </div>
        </InfoTooltip>
      );
    },
  },
  {
    id: 'last_trained',
    accessorKey: 'last_trained',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'brains.last_trained' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('brains.last_trained')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const lastTrained = row.original.last_trained;
      return lastTrained ? (
        <TimestampTooltip timestamp={lastTrained}>{timeFromNow(lastTrained)}</TimestampTooltip>
      ) : (
        '-'
      );
    },
  },
  {
    id: 'last_used_at',
    accessorKey: 'last_used_at',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'common.last_used' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('common.last_used')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const lastUsed = row.original.last_used_at;
      return lastUsed ? (
        <TimestampTooltip timestamp={lastUsed}>{timeFromNow(lastUsed)}</TimestampTooltip>
      ) : (
        '-'
      );
    },
  },
  {
    id: 'active_sessions',
    accessorKey: 'active_sessions',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'brains.active_sessions', defaultHidden: true },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('brains.active_sessions')}</SortableHeader>
    ),
    cell: ({ row }) => (
      <span className="text-foreground">{row.original.active_sessions ?? '-'}</span>
    ),
  },
  {
    id: 'starting_node_id',
    accessorKey: 'starting_node_id',
    enableSorting: false,
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'brains.starting_node', defaultHidden: true },
    header: () => t('brains.starting_node'),
    cell: ({ row }) => <StartingNodeCell agent={row.original} />,
  },
  {
    id: 'tools',
    enableSorting: false,
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'brains.tools', defaultHidden: true },
    header: () => t('brains.tools'),
    cell: ({ row }) => <ToolsCell agent={row.original} />,
  },
  {
    id: 'actions',
    enableHiding: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
    cell: ({ row }) => {
      const item = row.original;
      return <ActionsCell item={item} />;
    },
  },
];
